import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { X, Upload, Plus, Trash2, Image as ImageIcon, MapPin } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import axios from 'axios';

export default function ProductFormDialog({ open, onClose, product, onSave, categories }) {
  const isEditing = !!product;
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [regions, setRegions] = useState([]);
  const [formData, setFormData] = useState({
    name: '', description: '', price: '', sale_price: '', category_id: '', 
    region_name: '', region_description: '',
    inventory: '', sku: '', tags: '', weight: '', images: [], history_fact: '', international_delivery: false
  });
  const [variants, setVariants] = useState([{ size: '', price: '', inventory: '', weight: '', sku: '' }]);
  const [imageFiles, setImageFiles] = useState([null, null, null, null, null]);
  const [imagePreviews, setImagePreviews] = useState(['', '', '', '', '']);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Initialize autocomplete when dialog opens
  useEffect(() => {
    if (!open) return;
    
    const timer = setTimeout(() => {
      const input = document.getElementById('region_autocomplete_input');
      if (!input || !window.google?.maps?.places) return;
      
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ['(cities)'],
        componentRestrictions: { country: 'in' }
      });
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const name = place.formatted_address || place.name || '';
        setFormData(prev => ({ ...prev, region_name: name }));
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (open) {
      // Fetch broader regions
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/broader-regions`)
        .then(res => setRegions(res.data))
        .catch(err => console.error('Failed to fetch regions:', err));

      if (product) {
        setFormData({
          name: product.name || '',
          description: product.description || '',
          price: product.price?.toString() || '',
          sale_price: product.sale_price?.toString() || '',
          category_id: product.category_id || '',
          region_name: product.region_name || '',
          region_description: product.region_description || '',
          broader_region: product.broader_region || '',
          inventory: product.inventory?.toString() || '',
          sku: product.sku || '',
          tags: product.tags?.join(', ') || '',
          weight: product.weight?.toString() || '',
          images: product.images || [],
          history_fact: product.history_fact || '',
          international_delivery: product.international_delivery || false
        });
        
        // Set existing image previews
        const existingImages = product.images || [];
        const newPreviews = [...existingImages];
        while (newPreviews.length < 5) {
          newPreviews.push('');
        }
        setImagePreviews(newPreviews.slice(0, 5));
        
        // Set variants
        if (product.variants && product.variants.length > 0) {
          setVariants(product.variants);
        }
      } else {
        // Generate SKU for new product
        const timestamp = Date.now().toString().slice(-6);
        const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        const generatedSKU = `NH-${timestamp}${randomNum}`;
        
        setFormData({
          name: '', description: '', price: '', sale_price: '', category_id: '',
          region_name: '', region_description: '',
          inventory: '', sku: generatedSKU, tags: '', weight: '', images: [], history_fact: '', international_delivery: false
        });
        setImagePreviews(['', '', '', '', '']);
        setImageFiles([null, null, null, null, null]);
        setVariants([{ size: '', price: '', inventory: '', weight: '', sku: '' }]);
      }
    }
  }, [product, open]);

  const handleImageSelect = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      const newFiles = [...imageFiles];
      newFiles[index] = file;
      setImageFiles(newFiles);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const newPreviews = [...imagePreviews];
        newPreviews[index] = reader.result;
        setImagePreviews(newPreviews);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    const newFiles = [...imageFiles];
    newFiles[index] = null;
    setImageFiles(newFiles);

    const newPreviews = [...imagePreviews];
    newPreviews[index] = '';
    setImagePreviews(newPreviews);

    const newImages = [...formData.images];
    newImages[index] = '';
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const uploadImages = async () => {
    // Start with existing images, ensuring we maintain the correct array structure
    const uploadedUrls = [];
    const existingImages = formData.images || [];
    
    for (let i = 0; i < 5; i++) {
      if (imageFiles[i]) {
        // Upload new image
        const formDataUpload = new FormData();
        formDataUpload.append('file', imageFiles[i]);

        try {
          const response = await axios.post(`${API_URL}/api/upload-image`, formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedUrls[i] = response.data.url;
        } catch (error) {
          console.error('Failed to upload image', i, error);
          throw new Error(`Failed to upload image ${i + 1}`);
        }
      } else if (imagePreviews[i] && !imagePreviews[i].startsWith('data:')) {
        // Keep existing image URL (not a data URL preview)
        uploadedUrls[i] = imagePreviews[i];
      } else if (existingImages[i]) {
        // Fallback: keep from formData.images
        uploadedUrls[i] = existingImages[i];
      }
    }

    return uploadedUrls.filter(url => url && url.trim() !== '');
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setVariants(newVariants);
  };

  const addVariant = () => {
    setVariants([...variants, { size: '', price: '', inventory: '', weight: '', sku: '' }]);
  };

  const removeVariant = (index) => {
    if (variants.length > 1) {
      setVariants(variants.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.description || !formData.price || !formData.category_id || !formData.sku) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setUploadingImages(true);

    try {
      // Upload new images
      const imageUrls = await uploadImages();
      setUploadingImages(false);

      // Filter out empty variants
      const validVariants = variants.filter(v => v.size.trim() !== '');
      
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        category_id: formData.category_id,
        region_name: formData.region_name || null,
        region_description: formData.region_description || null,
        broader_region: formData.broader_region || null,
        inventory: parseInt(formData.inventory) || 0,
        sku: formData.sku,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        weight: formData.weight ? parseFloat(formData.weight) : null,
        images: imageUrls,
        history_fact: formData.history_fact || null,
        international_delivery: formData.international_delivery || false,
        variants: validVariants.length > 0 ? validVariants.map(v => ({
          size: v.size,
          price: parseFloat(v.price) || parseFloat(formData.price),
          inventory: parseInt(v.inventory) || 0,
          weight: parseFloat(v.weight) || 0,
          sku: v.sku || ''
        })) : undefined
      };

      await onSave(productData);
      onClose();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error(error.message || 'Failed to save product');
    } finally {
      setLoading(false);
      setUploadingImages(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEditing ? 'Edit Product' : 'Add New Product'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-2"><Label htmlFor="name">Product Name *</Label><Input id="name" value={formData.name} onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))} placeholder="Enter product name" required /></div>
          <div className="space-y-2"><Label htmlFor="description">Description *</Label><Textarea id="description" value={formData.description} onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))} placeholder="Enter product description" rows={4} required /></div>
          
          {/* History/Fun Fact */}
          <div className="space-y-2">
            <Label htmlFor="history_fact">History / Fun Fact (Optional)</Label>
            <Textarea 
              id="history_fact" 
              value={formData.history_fact} 
              onChange={(e) => setFormData(prev => ({...prev, history_fact: e.target.value}))} 
              placeholder="Add interesting history or fun facts about this product (e.g., traditional origin, cultural significance, interesting stories)" 
              rows={3}
            />
            <p className="text-xs text-gray-500">💡 This will be displayed beautifully on the product page to educate customers</p>
          </div>

          {/* International Delivery Checkbox */}
          <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <input
              type="checkbox"
              id="international_delivery"
              checked={formData.international_delivery}
              onChange={(e) => setFormData(prev => ({...prev, international_delivery: e.target.checked}))}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="international_delivery" className="text-sm font-medium text-gray-900 cursor-pointer">
              🌍 This product can be delivered outside India (International Delivery)
            </label>
          </div>

          
          {/* Image Upload Section */}
          <div className="space-y-3">
            <Label>Product Images (Upload up to 5 images)</Label>
            <div className="grid grid-cols-5 gap-3">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative border-2 border-dashed border-gray-300 rounded-lg p-2 hover:border-green-500 transition-colors">
                  {preview ? (
                    <div className="relative">
                      <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-24 cursor-pointer">
                      <ImageIcon className="w-8 h-8 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500">Upload</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageSelect(index, e)}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">💡 Tip: Upload images in the same size (recommended: 800x800px). Max 5MB per image.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="price">Base Price * (₹)</Label><Input id="price" type="number" step="0.01" min="0" value={formData.price} onChange={(e) => setFormData(prev => ({...prev, price: e.target.value}))} placeholder="0.00" required /></div>
            <div className="space-y-2"><Label htmlFor="sale_price">Sale Price (₹)</Label><Input id="sale_price" type="number" step="0.01" min="0" value={formData.sale_price} onChange={(e) => setFormData(prev => ({...prev, sale_price: e.target.value}))} placeholder="0.00" /></div>
          </div>

          {/* Variants Section */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <Label>Product Variants (Optional)</Label>
              <Button type="button" size="sm" variant="outline" onClick={addVariant} className="text-xs">
                <Plus className="w-3 h-3 mr-1" /> Add Variant
              </Button>
            </div>
            <div className="space-y-2">
              {variants.map((variant, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <Input 
                      placeholder="Size (e.g., 250g, 500g, 1kg)" 
                      value={variant.size} 
                      onChange={(e) => handleVariantChange(index, 'size', e.target.value)}
                    />
                  </div>
                  <div className="w-28">
                    <Input 
                      type="number" 
                      step="0.01" 
                      placeholder="Price (₹)" 
                      value={variant.price} 
                      onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                    />
                  </div>
                  <div className="w-20">
                    <Input 
                      type="number" 
                      placeholder="Stock" 
                      value={variant.inventory} 
                      onChange={(e) => handleVariantChange(index, 'inventory', e.target.value)}
                    />
                  </div>
                  <div className="w-24">
                    <Input 
                      type="number" 
                      step="0.01" 
                      placeholder="Weight (kg)" 
                      value={variant.weight} 
                      onChange={(e) => handleVariantChange(index, 'weight', e.target.value)}
                    />
                  </div>
                  <div className="w-32">
                    <Input 
                      placeholder="SKU" 
                      value={variant.sku} 
                      onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                    />
                  </div>
                  {variants.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeVariant(index)} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Collection and Region Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category_id">Collection *</Label>
              <select id="category_id" value={formData.category_id || ''} onChange={(e) => setFormData(prev => ({...prev, category_id: e.target.value}))} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" required>
                <option value="">Select a collection</option>
                {categories.map((cat) => (<option key={cat.id || cat._id} value={cat.id || cat._id}>{cat.name}</option>))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="region_autocomplete_input">
                <MapPin className="w-4 h-4 inline mr-1" />
                Region (Location)
              </Label>
              <input
                id="region_autocomplete_input"
                type="text"
                value={formData.region_name || ''} 
                onChange={(e) => setFormData(prev => ({...prev, region_name: e.target.value}))} 
                placeholder="Type city: Karaikudi, Madurai..."
                autoComplete="off"
                className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <p className="text-xs text-gray-500">📍 Type to get Google suggestions</p>
            </div>
          </div>

          {/* Region Description */}
          <div className="space-y-2">
            <Label htmlFor="region_description">Region Description (Optional)</Label>
            <Textarea 
              id="region_description" 
              value={formData.region_description} 
              onChange={(e) => setFormData(prev => ({...prev, region_description: e.target.value}))} 
              placeholder="Describe the region's significance, specialties, or cultural importance (e.g., 'Famous for traditional Chettinad cuisine and heritage architecture')" 
              rows={2}
            />
            <p className="text-xs text-gray-500">💡 Help customers understand why this region is special for this product</p>
          </div>

          {/* Broader Region for Shipping */}
          <div className="space-y-2">
            <Label htmlFor="broader_region">Broader Region (for Shipping) *</Label>
            <select
              id="broader_region"
              value={formData.broader_region || ''}
              onChange={(e) => setFormData(prev => ({...prev, broader_region: e.target.value}))}
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            >
              <option value="">Select Broader Region</option>
              {regions.map(region => (
                <option key={region._id || region.id} value={region.code}>
                  {region.code} - {region.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">🚚 Used for shipping zone calculation (e.g., TN-South, KA-North)</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="sku">SKU *</Label><Input id="sku" value={formData.sku} onChange={(e) => setFormData(prev => ({...prev, sku: e.target.value}))} placeholder="PROD-001" required /></div>
            <div className="space-y-2"><Label htmlFor="inventory">Inventory</Label><Input id="inventory" type="number" min="0" value={formData.inventory} onChange={(e) => setFormData(prev => ({...prev, inventory: e.target.value}))} placeholder="0" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="tags">Tags (comma separated)</Label><Input id="tags" value={formData.tags} onChange={(e) => setFormData(prev => ({...prev, tags: e.target.value}))} placeholder="eco-friendly, organic" /></div>
            <div className="space-y-2"><Label htmlFor="weight">Weight (kg)</Label><Input id="weight" type="number" step="0.01" min="0" value={formData.weight} onChange={(e) => setFormData(prev => ({...prev, weight: e.target.value}))} placeholder="0.00" /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" disabled={loading} className="btn-primary">
              {loading ? <>{uploadingImages ? 'Uploading images...' : 'Saving...'}</> : (isEditing ? 'Update Product' : 'Create Product')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
